import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getVerifiedLawyers } from "@/shared/db";
import {
  getEnabledCountries,
  type CountryConfig,
} from "@/shared/config/countries";
import { LawyerCard } from "../components/LawyerCard";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Select } from "@/shared/ui";

interface Lawyer {
  id: string;
  university: string;
  years_of_experience: number;
  practice_areas: string[];
  accept_clients: boolean;
  hourly_rate?: number;
  is_verified: boolean;
  profiles: {
    full_name: string;
    profile_image_url?: string;
    country: string;
  };
}

export const LawyerDirectoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [lawyers, setLawyers] = useState<Lawyer[]>([]);
  const [filteredLawyers, setFilteredLawyers] = useState<Lawyer[]>([]);
  const [countries, setCountries] = useState<CountryConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [practiceAreaFilter, setPracticeAreaFilter] = useState<string>("all");
  const [experienceFilter, setExperienceFilter] = useState<number>(0);
  const [acceptingClientsOnly, setAcceptingClientsOnly] = useState(false);

  // Load countries and lawyers in parallel; loading=false only when both complete
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      const [countriesResult, lawyersResult] = await Promise.allSettled([
        getEnabledCountries(),
        getVerifiedLawyers(),
      ]);
      if (cancelled) return;
      if (countriesResult.status === "rejected") {
        toast.error(t("public.lawyerDirectory.errors.loadCountries"));
      } else {
        setCountries(countriesResult.value);
      }
      if (lawyersResult.status === "rejected") {
        toast.error(t("public.lawyerDirectory.errors.loadError"));
      } else {
        setLawyers(lawyersResult.value as unknown as Lawyer[]);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const filterLawyers = useCallback(() => {
    let filtered = [...lawyers];

    // Filter by country
    if (countryFilter !== "all") {
      filtered = filtered.filter((l) => l.profiles.country === countryFilter);
    }

    // Filter by practice area
    if (practiceAreaFilter !== "all") {
      filtered = filtered.filter(
        (l) => l.practice_areas && l.practice_areas.includes(practiceAreaFilter)
      );
    }

    // Filter by experience
    if (experienceFilter > 0) {
      filtered = filtered.filter(
        (l) => l.years_of_experience >= experienceFilter
      );
    }

    // Filter by accepting clients
    if (acceptingClientsOnly) {
      filtered = filtered.filter((l) => l.accept_clients);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (l) =>
          l.profiles.full_name.toLowerCase().includes(term) ||
          l.university?.toLowerCase().includes(term)
      );
    }

    // Sort by experience (descending)
    filtered.sort((a, b) => b.years_of_experience - a.years_of_experience);

    setFilteredLawyers(filtered);
  }, [
    lawyers,
    searchTerm,
    countryFilter,
    practiceAreaFilter,
    experienceFilter,
    acceptingClientsOnly,
  ]);

  useEffect(() => {
    filterLawyers();
  }, [filterLawyers]);

  const practiceAreas = [
    { code: "civil", name: t("lawyer.practiceAreas.civil") },
    { code: "penal", name: t("lawyer.practiceAreas.penal") },
    { code: "laboral", name: t("lawyer.practiceAreas.laboral") },
    { code: "comercial", name: t("lawyer.practiceAreas.comercial") },
    { code: "fiscal", name: t("lawyer.practiceAreas.fiscal") },
    { code: "administrativo", name: t("lawyer.practiceAreas.administrativo") },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-tiber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-tiber-700">{t("public.lawyerDirectory.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-romance-50">
      {/* Enhanced Header with Brand Identity */}
      <div className="bg-tiber-600 text-white py-16 shadow-xl">
        <div className="max-w-7xl mx-auto px-4">
          {/* Logo and Branding */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
              <img
                src="/images/brand/lawgentia-logo-white.png"
                alt="Lawgentia"
                className="h-10 w-10"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div>
              <h1 className="text-4xl font-bold">
                {t("public.lawyerDirectory.title")}
              </h1>
              <p className="text-tiber-100 text-lg mt-1">
                {t("public.lawyerDirectory.subtitle")}
              </p>
            </div>
          </div>

          {/* Stats Banner */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-3xl font-bold">{filteredLawyers.length}</div>
              <div className="text-sm text-tiber-100">{t("public.lawyerDirectory.stats.availableLawyers")}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-3xl font-bold">{countries.length}</div>
              <div className="text-sm text-tiber-100">{t("public.lawyerDirectory.stats.countries")}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-3xl font-bold">{practiceAreas.length}</div>
              <div className="text-sm text-tiber-100">{t("public.lawyerDirectory.stats.practiceAreas")}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-3xl font-bold">
                {filteredLawyers.length > 0
                  ? `${Math.round(
                      (filteredLawyers.filter((l) => l.is_verified).length /
                        filteredLawyers.length) *
                        100
                    )}%`
                  : "0%"}
              </div>
              <div className="text-sm text-tiber-100">{t("public.lawyerDirectory.stats.verified")}</div>
            </div>
          </div>

          {/* Enhanced Search Bar */}
          <div className="bg-white rounded-lg p-2 flex items-center shadow-lg">
            <MagnifyingGlassIcon className="h-6 w-6 text-tiber-400 ml-3" />
            <input
              type="text"
              placeholder={t("public.lawyerDirectory.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-3 text-juridico focus:outline-none text-lg"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Enhanced Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg border-2 border-tiber-100 p-6 sticky top-4">
              <h3 className="text-xl font-bold text-tiber-900 mb-6 flex items-center">
                <FunnelIcon className="h-6 w-6 mr-2 text-tiber-600" />
                {t("public.lawyerDirectory.filters")}
              </h3>

              <div className="space-y-6">
                {/* Country Filter */}
                <div>
                  <Select
                    label={t("public.lawyerDirectory.country")}
                    options={[
                      { value: "all", label: t("public.lawyerDirectory.allCountries") },
                      ...countries.map((country) => ({
                        value: country.code,
                        label:
                          i18n.language === "es"
                            ? country.nameEs
                            : country.nameEn,
                      })),
                    ]}
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    className="w-full px-4 py-3 border-2 transition-all text-sm font-medium text-tiber-900 bg-white"
                  />
                </div>

                {/* Practice Area Filter */}
                <div>
                  <Select
                    label={t("public.lawyerDirectory.practiceArea")}
                    options={[
                      { value: "all", label: t("public.lawyerDirectory.allAreas") },
                      ...practiceAreas.map((area) => ({
                        value: area.code,
                        label: area.name,
                      })),
                    ]}
                    value={practiceAreaFilter}
                    onChange={(e) => setPracticeAreaFilter(e.target.value)}
                    className="w-full px-4 py-3 border-2 transition-all text-sm font-medium text-tiber-900 bg-white"
                  />
                </div>

                {/* Experience Filter */}
                <div>
                  <label className="block text-sm font-semibold text-tiber-900 mb-3">
                    {t("public.lawyerDirectory.minExperience")}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="5"
                    value={experienceFilter}
                    onChange={(e) =>
                      setExperienceFilter(parseInt(e.target.value))
                    }
                    className="w-full h-2 bg-tiber-400 rounded-lg appearance-none cursor-pointer accent-tiber-600"
                  />
                  <div className="flex justify-between text-xs text-tiber-600 mt-2 font-medium">
                    <span>0</span>
                    <span className="text-base font-bold text-tiber-900">
                      {experienceFilter}+ {t("public.lawyerDirectory.years")}
                    </span>
                    <span>30+</span>
                  </div>
                </div>

                {/* Accepting Clients Only */}
                <div className="pt-4 border-t border-tiber-100">
                  <label className="flex items-center space-x-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={acceptingClientsOnly}
                      onChange={(e) =>
                        setAcceptingClientsOnly(e.target.checked)
                      }
                      className="w-5 h-5 text-tiber-600 rounded focus:ring-2 focus:ring-tiber-500 border-2 border-tiber-300"
                    />
                    <span className="text-sm font-medium text-tiber-900 group-hover:text-tiber-700 transition-colors">
                      {t("public.lawyerDirectory.acceptingClientsOnly")}
                    </span>
                  </label>
                </div>

                {/* Reset Filters - Enhanced */}
                {(countryFilter !== "all" ||
                  practiceAreaFilter !== "all" ||
                  experienceFilter > 0 ||
                  acceptingClientsOnly) && (
                  <button
                    onClick={() => {
                      setCountryFilter("all");
                      setPracticeAreaFilter("all");
                      setExperienceFilter(0);
                      setAcceptingClientsOnly(false);
                    }}
                    className="w-full px-4 py-3 text-sm font-semibold text-tiber-600 hover:text-white hover:bg-tiber-600 border-2 border-tiber-600 rounded-lg transition-all duration-200"
                  >
                    {t("public.lawyerDirectory.clearFilters")}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Lawyers Grid */}
          <div className="lg:col-span-3">
            {/* Results Header */}
            <div className="mb-6 flex items-center justify-between bg-white rounded-lg shadow-sm p-4 border border-tiber-100">
              <h2 className="text-lg font-bold text-tiber-900 flex items-center gap-2">
                <UserGroupIcon className="h-6 w-6 text-tiber-600" />
                {t("public.lawyerDirectory.lawyersFound", {
                  count: filteredLawyers.length,
                })}
              </h2>
              <div className="text-sm text-tiber-600 font-medium">
                {filteredLawyers.length}{" "}
                {filteredLawyers.length === 1
                  ? t("public.lawyerDirectory.result")
                  : t("public.lawyerDirectory.results")}
              </div>
            </div>

            {filteredLawyers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredLawyers.map((lawyer, index) => (
                  <motion.div
                    key={lawyer.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <LawyerCard
                      lawyer={lawyer}
                      onClick={() => navigate(`/lawyers/${lawyer.id}`)}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg border-2 border-tiber-100 p-12 text-center">
                <UserGroupIcon className="h-20 w-20 text-tiber-300 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-tiber-900 mb-3">
                  {t("public.lawyerDirectory.noLawyers")}
                </h3>
                <p className="text-tiber-600 mb-6 max-w-md mx-auto">
                  {searchTerm ||
                  countryFilter !== "all" ||
                  practiceAreaFilter !== "all"
                    ? t("public.lawyerDirectory.tryAdjustingFilters")
                    : t("public.lawyerDirectory.noVerifiedLawyers")}
                </p>
                {(searchTerm ||
                  countryFilter !== "all" ||
                  practiceAreaFilter !== "all" ||
                  experienceFilter > 0) && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setCountryFilter("all");
                      setPracticeAreaFilter("all");
                      setExperienceFilter(0);
                      setAcceptingClientsOnly(false);
                    }}
                    className="px-6 py-3 bg-tiber-600 text-white font-semibold rounded-lg hover:bg-tiber-700 transition-colors"
                  >
                    {t("public.lawyerDirectory.clearFilters")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
